import React, { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

// Material-UI Icons
import {
  Add as AddIcon,
  Assessment as TimeReportIcon,
  Assignment as TaskIcon,
  AccountTree as DivisionIcon,
  AssignmentTurnedIn,
  CalendarMonth as CalendarIcon,
  EventNote as BiweeklyIcon,
  Cancel as CancelIcon,
  CheckCircleOutline,
  ChevronLeft,
  ChevronRight,
  Close,
  Dashboard as DashboardIcon,
  DeleteOutlined,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  Extension,
  Filter1Outlined,
  GridView as MatrixIcon,
  Groups as MeetingIcon,
  InfoOutlined,
  ListAlt as AllTaskIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  People as GroupsIcon,
  PriorityHigh as PriorityIcon,
  Search as SearchIcon,
  StarBorder as CustomViewIcon,
  Settings as SettingsIcon,
  Timer as TimeIcon,
  Today as TodayIcon,
  Workspaces,
} from '@mui/icons-material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

// Material-UI Components
import {
  AppBar,
  Avatar,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

// Internal Components
import AccuracyAnalysisReport from '../ReportComponent/AccuracyAnalysisReport';
import BiWeeklyReport from '../ReportComponent/BiWeeklyReport';
import DailyNotes from '../ReportComponent/DailyNotes';
import PerformanceSummary from '../ReportComponent/PerformanceSummary';
import TimeLogsReport from '../ReportComponent/TimeLogsReport';
import AllTaskView from '../TaskManagement/AllTasks';
import ClosedTask from '../TaskManagement/ClosedTask';
import CompletedTask from '../TaskManagement/CompletedTask';
import CompletionReport from '../ReportComponent/CompletionReport';
import CoreTask from '../TaskManagement/CoreTask';
import CustomViewPage from '../TaskManagement/CustomViewPage';
import DeletedTask from '../TaskManagement/DeletedTask';
import CalendarPage from '../CalendarComponent/CalendarPage';
import Home from './Home';
import Meetings from '../MeetingComponent/Meetings';
import PriorityList from '../TaskManagement/PriorityTask';
import ProfileView from '../UserComponent/ProfileView';
import ProjectListView from '../ProjectComponents/ProjectList';
import RemainingTasksView from '../TaskManagement/RemainingTasks';
import SettingView from '../SettingsComponent/SettingView';
import SprintListView from '../ProjectComponents/SprintList';
import TaskCreate from '../KeyComponents/TaskCreate';
import TaskDetailsPage from '../KeyComponents/TaskDetails';
import TaskDetailDrawer from '../KeyComponents/TaskDetailDrawer';
import { TaskOverlayProvider } from '../../utils/TaskOverlayContext';
import TaskManagerPage from '../TaskManagement/DoToday';
import TimeLog from '../TimeLogComponent/TimeLog';
import TrackDetailsPage from '../KeyComponents/TrackDetails';
import TrackListView from '../ProjectComponents/TrackList';
import DivisionListView from '../ProjectComponents/DivisionList';
import MatrixView from '../PlanningComponent/MatrixView';

// Context and Utilities
import { useThemeMode } from '../../theme/ThemeProvider';
import { useView } from '../../utils/PersonalViewProvider';
import { useCustomViews } from '../../utils/CustomViewsProvider';
import CustomViewDialog from '../dialogs/CustomViewDialog';
import SearchDialog from '../dialogs/SearchDialog';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useAssignees } from '../../utils/AssigneeContext';
import useCurrentUser from '../../utils/useCurrentUser';
import { MEDIA_URL } from '../../services/api';
import TrackSprintDetail from '../ProjectComponents/TrackSprintDetail';

const drawerWidth = 240;
const closedDrawerWidth = 60;

/**
 * @typedef {Object} MenuItem
 * @property {string} text - Display text for the menu item
 * @property {React.ReactElement} icon - Icon component for the menu item
 * @property {string} path - Navigation path for the menu item
 * @property {boolean} [divider] - Whether to show a divider after this item
 */

/**
 * Styled main content area that adjusts based on drawer state
 */
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(2),
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open ? {
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    } : {
      width: `calc(100% - ${closedDrawerWidth}px)`,
    }),
  })
);

/**
 * Styled drawer header component
 */
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

/**
 * Styled avatar with unassigned state support
 */
const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'isUnassigned',
})(({ theme, isUnassigned }) => ({
  backgroundColor: isUnassigned ? 'transparent' : theme.palette.primary.main,
  color: isUnassigned
    ? theme.palette.text.secondary
    : theme.palette.primary.contrastText,
  width: 32,
  height: 32,
  fontSize: '0.875rem',
  border: isUnassigned ? `2px dashed ${theme.palette.text.secondary}` : 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}));

/**
 * Styled menu item with selection state
 */
const StyledMenuItem = styled(MenuItem)(({ theme, selected }) => ({
  gap: '8px',
  padding: '8px 16px',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}));

/**
 * Main dashboard component that provides navigation and layout structure
 * @component
 * @returns {React.ReactElement} The dashboard layout with navigation and routing
 */
const Dashboard = () => {
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [assigneeMenuAnchorEl, setAssigneeMenuAnchorEl] = useState(null);
  const [workspaceAnchorEl, setWorkspaceAnchorEl] = useState(null);
  const [isTaskCreateDialogOpen, setIsTaskCreateDialogOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);

  // Navbar section expand state + Personalized View dialog state.
  const [systemOpen, setSystemOpen] = useState(true);
  const [reportsOpen, setReportsOpen] = useState(true);
  const [personalOpen, setPersonalOpen] = useState(true);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);

  // Context hooks
  const { selectedAssignee, updateSelectedAssignee } = useView();

  const { workspaces, currentWorkspace, selectWorkspace } = useWorkspace();
  const { assignees } = useAssignees();
  const { user: currentUser } = useCurrentUser();
  const { views: customViews, deleteView } = useCustomViews();

  const handleAddCustomView = () => {
    setEditingView(null);
    setCustomDialogOpen(true);
  };

  const handleEditCustomView = (view) => {
    setEditingView(view);
    setCustomDialogOpen(true);
  };

  const handleDeleteCustomView = async (view) => {
    try {
      await deleteView(view.id);
      // Leave the page if the deleted view is the one being shown.
      if (location.pathname === `/custom-view/${view.slug}`) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error deleting view:', error);
    }
  };

  const handleCustomViewSaved = (saved) => {
    // Open the new view after creating (not on edit).
    if (saved?.slug && !editingView) {
      navigate(`/custom-view/${saved.slug}`);
    }
  };

  // Initials fallback for the profile avatar — same logic as the assignee pill.
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const profileName = currentUser?.full_name || currentUser?.username || '';
  const profileImage = currentUser?.image
    ? currentUser.image.startsWith('http')
      ? currentUser.image
      : `${MEDIA_URL}${currentUser.image}`
    : undefined;

  /**
   * Toggles the drawer open/closed state
   */
  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  /**
   * Opens the profile menu
   * @param {React.MouseEvent} event - The click event
   */
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the profile menu
   */
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Opens the workspace selection menu
   * @param {React.MouseEvent} event - The click event
   */
  const handleWorkspaceMenuOpen = (event) => {
    setWorkspaceAnchorEl(event.currentTarget);
  };

  /**
   * Closes the workspace selection menu
   */
  const handleWorkspaceMenuClose = () => {
    setWorkspaceAnchorEl(null);
  };

  /**
   * Navigates to a specific path and closes the profile menu
   * @param {string} path - The navigation path
   */
  const handleNavigate = (path) => {
    navigate(path);
    handleProfileMenuClose();
  };

  /**
   * Handles user logout by clearing token and redirecting to login
   */
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  /**
   * Opens the assignee selection menu
   * @param {React.MouseEvent} event - The click event
   */
  const handleAssigneeButtonClick = (event) => {
    setAssigneeMenuAnchorEl(event.currentTarget);
  };

  /**
   * Closes the assignee selection menu
   */
  const handleAssigneeMenuClose = () => {
    setAssigneeMenuAnchorEl(null);
  };

  /**
   * Selects which user's tasks to show. Pass a user id to scope to that
   * person, or null to show everyone's tasks ("All Users").
   * @param {number|null} assigneeId
   */
  const handleAssigneeSelect = (assigneeId) => {
    updateSelectedAssignee(assigneeId);
    handleAssigneeMenuClose();
  };

  /**
   * Opens the task creation dialog
   */
  const handleTaskCreateDialogOpen = () => {
    setIsTaskCreateDialogOpen(true);
  };

  /**
   * Closes the task creation dialog
   */
  const handleTaskCreateDialogClose = () => {
    setIsTaskCreateDialogOpen(false);
  };

  /**
   * Handles successful task creation by triggering a refresh
   */
  const handleTaskCreated = () => {
    setTaskRefreshKey(prevKey => prevKey + 1);
  };

  /**
   * Menu items configuration for the navigation drawer
   * @type {MenuItem[]}
   */
  // Top-level navigation (everything outside the task-list pages).
  const topMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', divider: true },
    { text: 'Divisions', icon: <DivisionIcon />, path: '/division-list', divider: true },
  ];

  // Calendar / scheduling pages, placed after the view sections.
  const scheduleMenuItems = [
    { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
    { text: 'Meetings', icon: <MeetingIcon />, path: '/meetings' },
    { text: 'Time Logs', icon: <TimeIcon />, path: '/time-logs', divider: true },
  ];

  // The Planning section (always expanded) and its views.
  const planningMenuItems = [
    { text: 'Matrix View', icon: <MatrixIcon />, path: '/planning/matrix' },
    { text: 'Track Sprint Detail', icon: <Extension />, path: '/track-sprint-detail' },
  ];

  // The Reports section, grouped under a collapsible "Reports" header.
  const reportMenuItems = [
    { text: 'Completion Report', icon: <CheckCircleOutline />, path: '/reports/complete-report' },
    { text: 'Accuracy Report', icon: <Filter1Outlined />, path: '/reports/accuracy-report' },
    { text: 'Time Logs Report', icon: <TimeReportIcon />, path: '/reports/time-logs-report' },
    { text: 'Biweekly Review', icon: <BiweeklyIcon />, path: '/reports/biweekly-review' },
    { text: 'Daily Notes', icon: <BiweeklyIcon />, path: '/reports/daily-notes' },
    { text: 'Performance Summary', icon: <TimeReportIcon />, path: '/reports/performance-summary' },
  ];

  // The fixed task-list pages, grouped under the "System View" section.
  const systemMenuItems = [
    { text: 'Do Today', icon: <TodayIcon />, path: '/do-today' },
    { text: 'Priority List', icon: <PriorityIcon />, path: '/priority-list' },
    { text: 'Remaining Task', icon: <TaskIcon />, path: '/remaining-task' },
    { text: 'Core Task', icon: <AssignmentTurnedIn />, path: '/core-task' },
    { text: 'Completed Task', icon: <CheckCircleOutline />, path: '/completed-task' },
    { text: 'All Task', icon: <AllTaskIcon />, path: '/all-task' },
    { text: 'Closed Task', icon: <CancelIcon />, path: '/closed-task' },
    { text: 'Deleted Task', icon: <DeleteOutlined />, path: '/deleted-task' },
  ];

  // Resolve the current page's display name from the route, shown in the AppBar
  // title as "Workspace - Page Name".
  const allNavItems = [
    ...topMenuItems,
    ...planningMenuItems,
    ...systemMenuItems,
    ...scheduleMenuItems,
    ...reportMenuItems,
  ];

  const getPageName = () => {
    const path = location.pathname;
    const navMatch = allNavItems.find((item) => item.path === path);
    if (navMatch) return navMatch.text;
    if (path.startsWith('/custom-view/')) {
      const slug = path.slice('/custom-view/'.length);
      return customViews.find((view) => view.slug === slug)?.name || 'Custom View';
    }
    if (path.startsWith('/dashboard/profile')) return 'Profile';
    if (path.startsWith('/dashboard/settings')) return 'Settings';
    if (path.startsWith('/tasks/')) return 'Task Details';
    if (path.startsWith('/tracks/')) return 'Track Details';
    return '';
  };

  const pageName = getPageName();

  // Resolve the selected assignee to a user object for display. Falls back to
  // the current user when they're the selection but aren't in the workspace
  // assignee list yet (e.g. before that list has loaded).
  const resolveAssignee = (id) =>
    assignees.find((a) => a.id === id) ||
    (currentUser?.id === id ? currentUser : null);

  const selectedAssigneeObj =
    selectedAssignee != null ? resolveAssignee(selectedAssignee) : null;

  const assigneeLabel =
    selectedAssignee == null
      ? 'All Users'
      : selectedAssigneeObj?.full_name ||
        selectedAssigneeObj?.username ||
        'User';

  const assigneeAvatarSrc = (id) => `${MEDIA_URL}/media/user_${id}/avatar`;

  /**
   * Assignee selection menu: pick whose tasks to view across the app. The
   * first entry clears the filter to show everyone's tasks.
   */
  const assigneeMenu = (
    <Menu
      anchorEl={assigneeMenuAnchorEl}
      open={Boolean(assigneeMenuAnchorEl)}
      onClose={handleAssigneeMenuClose}
      slotProps={{ paper: { sx: { minWidth: 220, maxHeight: 380 } } }}
    >
      <StyledMenuItem
        selected={selectedAssignee == null}
        onClick={() => handleAssigneeSelect(null)}
      >
        <ListItemIcon>
          <GroupsIcon fontSize="small" />
        </ListItemIcon>
        All Users
      </StyledMenuItem>
      <Divider />
      {assignees.map((assignee) => {
        const name = assignee.full_name || assignee.username || 'User';
        const isYou = assignee.id === currentUser?.id;
        return (
          <StyledMenuItem
            key={assignee.id}
            selected={assignee.id === selectedAssignee}
            onClick={() => handleAssigneeSelect(assignee.id)}
          >
            <Avatar
              src={assigneeAvatarSrc(assignee.id)}
              alt={name}
              sx={{ width: 26, height: 26, fontSize: '0.75rem' }}
            >
              {getInitials(name)}
            </Avatar>
            {isYou ? `${name} (You)` : name}
          </StyledMenuItem>
        );
      })}
    </Menu>
  );

  // Renders one nav entry; `endAction` shows trailing controls when expanded.
  const renderNavItem = ({ key, icon, text, path, onClick, endAction }) => {
    const isActive = path && location.pathname === path;
    return (
      <ListItem
        key={key || text}
        disablePadding
        secondaryAction={open && endAction ? endAction : undefined}
      >
        <ListItemButton
          onClick={onClick || (() => navigate(path))}
          selected={Boolean(isActive)}
          sx={{
            minHeight: 48,
            justifyContent: open ? 'initial' : 'center',
            px: 2.5,
            backgroundColor: isActive ? theme.palette.action.selected : 'transparent',
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
              '& .MuiListItemIcon-root': { color: theme.palette.primary.contrastText },
            },
            '&:hover': {
              backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
              color: isActive ? theme.palette.primary.contrastText : 'inherit',
            }}
          >
            {icon}
          </ListItemIcon>
          {open && <ListItemText primary={text} primaryTypographyProps={{ noWrap: true }} />}
        </ListItemButton>
      </ListItem>
    );
  };

  /** Collapsible section header (only shown when the drawer is expanded). */
  const renderSectionHeader = (label, expanded, onToggle) => (
    <ListItemButton onClick={onToggle} sx={{ py: 0.5 }}>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          variant: 'overline',
          sx: { fontWeight: 700, color: 'text.secondary', letterSpacing: 1 },
        }}
      />
      {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
    </ListItemButton>
  );

  /**
   * Navigation drawer content component
   */
  const drawer = (
    <Box>
      <DrawerHeader>
        {open && (
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, ml: 2 }}
          >
            Prakriya
          </Typography>
        )}
        <IconButton onClick={handleDrawerToggle}>
          {open ? <ChevronLeft /> : <MenuIcon />}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List>
        {topMenuItems.map((item, index) => (
          <React.Fragment key={item.text}>
            {renderNavItem(item)}
            {item.divider && <Divider />}
          </React.Fragment>
        ))}

        {/* Planning: always-expanded section */}
        {open && (
          <ListItemButton disableRipple sx={{ py: 0.5, cursor: 'default' }}>
            <ListItemText
              primary="Planning"
              primaryTypographyProps={{
                variant: 'overline',
                sx: { fontWeight: 700, color: 'text.secondary', letterSpacing: 1 },
              }}
            />
          </ListItemButton>
        )}
        {planningMenuItems.map((item) => renderNavItem(item))}

        <Divider />

        {/* System View: the fixed task-list pages */}
        {open && renderSectionHeader('System View', systemOpen, () => setSystemOpen((v) => !v))}
        <Collapse in={open ? systemOpen : true} timeout="auto" unmountOnExit>
          {systemMenuItems.map((item) => renderNavItem(item))}
        </Collapse>

        <Divider />

        {/* Personalized Views: user-defined custom pages */}
        {open ? (
          <ListItemButton onClick={() => setPersonalOpen((v) => !v)} sx={{ py: 0.5 }}>
            <ListItemText
              primary="My Views"
              primaryTypographyProps={{
                variant: 'overline',
                sx: { fontWeight: 700, color: 'text.secondary', letterSpacing: 1 },
              }}
            />
            <Tooltip title="Add my view">
              <IconButton
                size="small"
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddCustomView();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {personalOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
        ) : (
          renderNavItem({
            key: 'add-custom-view',
            icon: <AddIcon />,
            text: 'Add View',
            onClick: handleAddCustomView,
          })
        )}
        <Collapse in={open ? personalOpen : true} timeout="auto" unmountOnExit>
          {customViews.map((view) =>
            renderNavItem({
              key: `cv-${view.id}`,
              icon: <CustomViewIcon />,
              text: view.name,
              path: `/custom-view/${view.slug}`,
              endAction: (
                <Box sx={{ display: 'flex' }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleEditCustomView(view)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDeleteCustomView(view)}>
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            }),
          )}
          {open && customViews.length === 0 && (
            <Typography variant="caption" sx={{ px: 2.5, py: 1, display: 'block', color: 'text.disabled' }}>
              No personalized views yet.
            </Typography>
          )}
        </Collapse>

        <Divider />

        {/* Calendar / scheduling pages */}
        {scheduleMenuItems.map((item) => (
          <React.Fragment key={item.text}>
            {renderNavItem(item)}
            {item.divider && <Divider />}
          </React.Fragment>
        ))}

        {/* Reports: collapsible section grouping the report pages */}
        {open && renderSectionHeader('Reports', reportsOpen, () => setReportsOpen((v) => !v))}
        <Collapse in={open ? reportsOpen : true} timeout="auto" unmountOnExit>
          {reportMenuItems.map((item) => renderNavItem(item))}
        </Collapse>
      </List>
    </Box>
  );

  /**
   * Profile menu component
   */
  const profileMenu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleProfileMenuClose}
      sx={{ mt: '45px' }}
    >
      <MenuItem onClick={() => handleNavigate('/dashboard/profile')}>
        <ListItemIcon>
          <PersonIcon fontSize="small" />
        </ListItemIcon>
        View Profile
      </MenuItem>
      <MenuItem onClick={() => handleNavigate('/dashboard/settings')}>
        <ListItemIcon>
          <SettingsIcon fontSize="small" />
        </ListItemIcon>
        Settings
      </MenuItem>
      <MenuItem onClick={handleWorkspaceMenuOpen}>
        <ListItemIcon>
          <Workspaces fontSize="small" />
        </ListItemIcon>
        Workspaces
      </MenuItem>
      <Menu
        anchorEl={workspaceAnchorEl}
        open={Boolean(workspaceAnchorEl)}
        onClose={handleWorkspaceMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {workspaces?.length > 0 ? (
          workspaces.map((workspace) => (
            <StyledMenuItem
              key={workspace.id}
              onClick={() => {
                selectWorkspace(workspace);
                handleWorkspaceMenuClose();
              }}
              selected={currentWorkspace?.id === workspace.id}
            >
              <ListItemIcon>
                <Workspaces fontSize="small" />
              </ListItemIcon>
              {workspace.name}
            </StyledMenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemIcon>
              <InfoOutlined fontSize="small" />
            </ListItemIcon>
            No workspaces available
          </MenuItem>
        )}
      </Menu>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <TaskOverlayProvider>
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          width: `calc(100% - ${open ? drawerWidth : closedDrawerWidth}px)`,
          marginLeft: open ? drawerWidth : closedDrawerWidth,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" noWrap component="div">
              {currentWorkspace?.name}
              {pageName ? ` - ${pageName}` : ''}
            </Typography>
          </Box>
          <Box
            onClick={() => setIsSearchDialogOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              mx: 2,
              flexGrow: 1,
              maxWidth: 420,
              cursor: 'pointer',
              borderRadius: 1,
              border: 1,
              borderColor: 'rgba(255, 255, 255, 0.4)',
              color: 'inherit',
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.22)' },
            }}
          >
            <SearchIcon fontSize="small" />
            <Typography variant="body2" noWrap sx={{ opacity: 0.85 }}>
              Search tasks, action items and notes…
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              onClick={handleTaskCreateDialogOpen}
              sx={{ border: 1, mr: 1 }}
            >
              <AddIcon />
            </IconButton>
            <Tooltip title={`Viewing: ${assigneeLabel} — click to change`}>
              <IconButton onClick={handleAssigneeButtonClick} sx={{ padding: 0.5 }}>
                {selectedAssignee == null ? (
                  <StyledAvatar isUnassigned sx={{ width: 32, height: 32 }}>
                    <GroupsIcon sx={{ fontSize: 20 }} />
                  </StyledAvatar>
                ) : (
                  <Avatar
                    src={assigneeAvatarSrc(selectedAssignee)}
                    alt={assigneeLabel}
                    sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
                  >
                    {getInitials(assigneeLabel)}
                  </Avatar>
                )}
              </IconButton>
            </Tooltip>
            {assigneeMenu}
            <IconButton color="inherit" onClick={toggleTheme}>
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <IconButton onClick={handleProfileMenuOpen} sx={{ padding: 0.5 }}>
              <Avatar
                src={profileImage}
                alt={profileName}
                sx={{ width: 32, height: 32 }}
              >
                {getInitials(profileName)}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: open ? drawerWidth : closedDrawerWidth,
          flexShrink: 0,
        }}
      >
        <Drawer
          sx={{
            width: open ? drawerWidth : closedDrawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: open ? drawerWidth : closedDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              overflowX: 'hidden',
              borderRight: 1,
              borderColor: 'divider',
              boxSizing: 'border-box',
            },
          }}
          variant="permanent"
          anchor="left"
          open={open}
        >
          {drawer}
        </Drawer>
      </Box>

      {profileMenu}

      <Main open={open}>
        <Toolbar />
        <Routes key={location.key}>
          <Route index element={<Home />} />
          <Route
            path="dashboard/profile"
            element={<ProfileView key={location.key} />}
          />
          <Route
            path="dashboard/settings"
            element={<SettingView key={location.key} />}
          />
          <Route
            path="do-today"
            element={<TaskManagerPage key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="priority-list"
            element={<PriorityList key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="all-task"
            element={<AllTaskView key={`${location.key}-${taskRefreshKey}`} taskRefreshTrigger={taskRefreshKey} />}
          />
          <Route
            path="remaining-task"
            element={<RemainingTasksView key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="core-task"
            element={<CoreTask key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="completed-task"
            element={<CompletedTask key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="closed-task"
            element={<ClosedTask key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="deleted-task"
            element={<DeletedTask key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="custom-view/:slug"
            element={<CustomViewPage key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="project-list"
            element={<ProjectListView key={location.key} />}
          />
          <Route
            path="tasks/:taskId"
            element={<TaskDetailsPage key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="track-list"
            element={<TrackListView key={location.key} />}
          />
          <Route
            path="division-list"
            element={<DivisionListView key={location.key} />}
          />
          <Route
            path="planning/matrix"
            element={<MatrixView key={`${location.key}-${taskRefreshKey}`} />}
          />
          <Route
            path="sprint-list"
            element={<SprintListView key={location.key} />}
          />
          <Route
            path="track-sprint-detail"
            element={<TrackSprintDetail key={location.key} />}
          />
          <Route path="calendar" element={<CalendarPage key={location.key} />} />
          <Route path="meetings" element={<Meetings key={location.key} />} />
          <Route path="time-logs" element={<TimeLog key={location.key} />} />
          <Route
            path="tracks/:trackId"
            element={<TrackDetailsPage key={location.key} />}
          />
          <Route path="reports/complete-report" element={<CompletionReport key={location.key} />} />
          <Route path="/reports/accuracy-report" element={<AccuracyAnalysisReport key={location.key} />} />
          <Route path="reports/time-logs-report" element={<TimeLogsReport key={location.key} />} />
          <Route path="reports/biweekly-review" element={<BiWeeklyReport key={location.key} />} />
          <Route path="reports/daily-notes" element={<DailyNotes key={location.key} />} />
          <Route path="reports/performance-summary" element={<PerformanceSummary key={location.key} />} />
          <Route path="*" element={<Navigate to="" replace />} />
        </Routes>
      </Main>
      <TaskCreate
        open={isTaskCreateDialogOpen}
        onClose={handleTaskCreateDialogClose}
        onTaskCreated={handleTaskCreated}
      />
      <CustomViewDialog
        open={customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
        view={editingView}
        onSaved={handleCustomViewSaved}
      />
      <SearchDialog
        open={isSearchDialogOpen}
        onClose={() => setIsSearchDialogOpen(false)}
      />
    </Box>
      <TaskDetailDrawer />
    </TaskOverlayProvider>
  );
};

export default Dashboard;
